<script setup>
import { moneyFull } from 'dashboard/helper/currencyFormat';
import { ref, computed } from 'vue';

const props = defineProps({
  columns: { type: Array, required: true },
  // [{ key: 'name', label: 'Nome', format: 'text', align: 'left' }]
  rows: { type: Array, default: () => [] },
  sortable: { type: Boolean, default: true },
  emptyText: { type: String, default: '' },
  exportable: { type: Boolean, default: true },
  exportFilename: { type: String, default: 'relatorio' },
  // Destaca o top 3 da ordenacao atual com medalhas (rankings).
  podium: { type: Boolean, default: false },
});

const sortKey = ref('');
const sortDir = ref('desc');

const toggleSort = key => {
  if (!props.sortable) return;
  if (sortKey.value === key) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortKey.value = key;
    sortDir.value = 'desc';
  }
};

const sortedRows = computed(() => {
  if (!sortKey.value || !props.sortable) return props.rows;
  return [...props.rows].sort((a, b) => {
    const av = a[sortKey.value];
    const bv = b[sortKey.value];
    const cmp =
      typeof av === 'string' ? av.localeCompare(bv) : (av || 0) - (bv || 0);
    return sortDir.value === 'asc' ? cmp : -cmp;
  });
});

const exportCSV = () => {
  if (!props.rows.length) return;
  const header = props.columns.map(c => c.label).join(';');
  const body = sortedRows.value
    .map(row =>
      props.columns
        .map(col => {
          const val = row[col.key];
          if (val == null) return '';
          if (col.format === 'currency') return Number(val).toFixed(2);
          if (col.format === 'percent') return `${Number(val).toFixed(1)}%`;
          return String(val).replace(/;/g, ',');
        })
        .join(';')
    )
    .join('\n');
  const bom = '\uFEFF';
  const blob = new Blob([bom + header + '\n' + body], {
    type: 'text/csv;charset=utf-8;',
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${props.exportFilename}_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

// Mini-bar (padrao Talkdesk): coluna com `bar: true` desenha barra
// proporcional ao maximo da coluna atras do valor.
const barMax = computed(() => {
  const maxes = {};
  props.columns
    .filter(c => c.bar)
    .forEach(c => {
      maxes[c.key] = Math.max(...props.rows.map(r => Number(r[c.key]) || 0), 1);
    });
  return maxes;
});

const barWidth = (col, value) =>
  `${Math.round(((Number(value) || 0) / barMax.value[col.key]) * 100)}%`;

const PODIUM_MEDALS = ['🥇', '🥈', '🥉'];
const podiumMedal = i => (props.podium ? PODIUM_MEDALS[i] : null);

const formatCell = (value, format) => {
  if (value == null) return '-';
  switch (format) {
    case 'currency':
      return moneyFull(value);
    case 'percent':
      return `${Number(value).toFixed(1)}%`;
    case 'time': {
      const h = Number(value);
      if (h <= 0) return '-';
      if (h >= 24) return `${Math.floor(h / 24)}d ${Math.round(h % 24)}h`;
      if (h >= 1) return `${Math.floor(h)}h ${Math.round((h % 1) * 60)}m`;
      return `${Math.round(h * 60)}m`;
    }
    case 'number':
      return Number(value).toLocaleString('pt-BR');
    default:
      return value;
  }
};
</script>

<template>
  <div class="overflow-x-auto">
    <div v-if="exportable && rows.length > 0" class="flex justify-end mb-2">
      <button
        class="flex items-center gap-1 text-[11px] font-medium text-n-slate-9 hover:text-n-slate-11 dark:hover:text-n-slate-7 transition-colors"
        @click="exportCSV"
      >
        <span class="i-lucide-download size-3.5" />
        {{ $t('REPORT.EXPORT_CSV') }}
      </button>
    </div>
    <table class="w-full text-sm">
      <thead>
        <tr class="border-b border-n-weak">
          <th
            v-for="col in columns"
            :key="col.key"
            class="py-2.5 px-3 text-[10px] font-semibold text-n-slate-9 uppercase tracking-[0.07em]"
            :class="[
              col.align === 'right'
                ? 'text-right'
                : col.align === 'center'
                  ? 'text-center'
                  : 'text-left',
              sortable
                ? 'cursor-pointer select-none hover:text-n-slate-11'
                : '',
            ]"
            @click="toggleSort(col.key)"
          >
            <span class="inline-flex items-center gap-1">
              {{ col.label }}
              <span
                v-if="sortable && sortKey === col.key"
                :class="
                  sortDir === 'asc'
                    ? 'i-lucide-chevron-up'
                    : 'i-lucide-chevron-down'
                "
                class="size-3"
              />
            </span>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(row, i) in sortedRows"
          :key="i"
          class="border-b border-n-weak/60 even:bg-n-slate-12/[0.025] hover:bg-n-slate-12/[0.05] transition-colors"
        >
          <td
            v-for="(col, colIdx) in columns"
            :key="col.key"
            class="py-2.5 px-3 text-n-slate-12"
            :class="
              col.align === 'right'
                ? 'text-right tabular-nums'
                : col.align === 'center'
                  ? 'text-center'
                  : 'text-left'
            "
          >
            <slot :name="col.key" :row="row" :value="row[col.key]">
              <span v-if="colIdx === 0 && podiumMedal(i)" class="mr-1">{{
                podiumMedal(i)
              }}</span>
              <template v-if="col.bar">
                <span class="block">{{
                  formatCell(row[col.key], col.format)
                }}</span>
                <span
                  class="block h-1 mt-0.5 rounded-full bg-n-slate-4 overflow-hidden"
                >
                  <span
                    class="block h-full rounded-full bg-n-blue-9"
                    :style="{ width: barWidth(col, row[col.key]) }"
                  />
                </span>
              </template>
              <template v-else>
                {{ formatCell(row[col.key], col.format) }}
              </template>
            </slot>
          </td>
        </tr>
        <tr v-if="rows.length === 0">
          <td :colspan="columns.length" class="py-8 text-center text-n-slate-9">
            {{ emptyText || $t('REPORT.NO_DATA') }}
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>
